// Lib
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debugToolProperty } from '../../debug/store/debugToolSelector';
import { DEBUG_TOOLS } from '../../debug/store/debugToolConstants';
import { getCurrentlyEditingEditorKey } from '../../element/selection/currentlyEditingSelector';
import virtualKeyboardHeightSingleton from './virtualKeyboardHeightSingleton';

const hasSelection = (selection: Selection | null) => {
    return (
        selection &&
        selection.anchorNode &&
        // @ts-ignore isContentEditable is not in the types
        (selection.anchorNode.nodeName === '#text' || selection.anchorNode.isContentEditable)
    );
};

const SIMULATED_KEYBOARD_HEIGHT = 336;

export const useSimulateMobileKeyboard = ({
    onKeyboardWillShow,
    onKeyboardDidShow,
    onKeyboardWillHide,
}: {
    onKeyboardWillShow: (keyboardHeight: number) => void;
    onKeyboardDidShow: (keyboardHeight: number) => void;
    onKeyboardWillHide: () => void;
}): boolean => {
    const simulateMobileKeyboard = useSelector(debugToolProperty(DEBUG_TOOLS.MOBILE, 'simulateMobileKeyboard'));
    const currentlyEditingEditorKey = useSelector(getCurrentlyEditingEditorKey);

    const hasSelectionRef = useRef(false);

    useEffect(() => {
        if (!simulateMobileKeyboard) {
            virtualKeyboardHeightSingleton.setHeight(0);
            return;
        }

        virtualKeyboardHeightSingleton.setHeight(SIMULATED_KEYBOARD_HEIGHT);
        const onSelectionChange = () => {
            const newSelection = document.getSelection();
            const newHasSelection = hasSelection(newSelection);

            // selecting
            if (!hasSelectionRef.current && newHasSelection) {
                hasSelectionRef.current = true;

                onKeyboardWillShow(SIMULATED_KEYBOARD_HEIGHT);

                setTimeout(function () {
                    onKeyboardDidShow(SIMULATED_KEYBOARD_HEIGHT);
                }, 100);
            }
        };

        document.addEventListener('selectionchange', onSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', onSelectionChange);
        };
    }, [simulateMobileKeyboard]);

    useEffect(() => {
        if (!simulateMobileKeyboard) return;

        if (!currentlyEditingEditorKey) {
            hasSelectionRef.current = false;

            onKeyboardWillHide();
        }
    }, [currentlyEditingEditorKey]);

    return simulateMobileKeyboard;
};

export default useSimulateMobileKeyboard;
